import { useEffect, useRef, useState } from 'react';
import { IoChevronDown, IoChevronUp, IoClose, IoMenu } from 'react-icons/io5';
import { createPortal } from 'react-dom';
import { MenuItems } from './MenuItems';
import { useLazyGetUserProfileQuery } from '@/store/api/userApi';

export const ProfileMenu = () => {
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [fetchUserProfile, { data: user }] = useLazyGetUserProfileQuery();

  useEffect(() => {
    fetchUserProfile();
  }, [fetchUserProfile]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsProfileOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  // Prevent background scrolling when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const handleNavigate = () => {
    setIsProfileOpen(false);
    setIsMobileMenuOpen(false);
  };

  const userName =
    [user?.firstname, user?.lastname].filter(Boolean).join(' ') || user?.email || 'User';
  const avatarSrc = user?.profileImage || '/images/home/backPerson.png';

  return (
    <>
      <div className="relative flex items-center gap-3 z-50" ref={dropdownRef}>
        <div className="hidden md:block">
          <button
            onClick={() => setIsProfileOpen(!isProfileOpen)}
            className="flex items-center gap-2"
          >
            <div className="w-10 h-10 overflow-hidden border-2 border-[#2A65B4] rounded-full">
              <img src={avatarSrc} className="object-cover w-full h-full" alt="User" />
            </div>

            <span className="text-2xl text-white">{userName}</span>
            {isProfileOpen ? <IoChevronUp size={24} /> : <IoChevronDown size={24} />}
          </button>

          {isProfileOpen && (
            <div className="absolute right-0 w-56 bg-[#000001] border-2 border-[#131F6D] rounded-[10px] overflow-hidden shadow-2xl">
              <MenuItems onNavigate={handleNavigate} />
            </div>
          )}
        </div>

        <button
          className="text-3xl text-white md:hidden"
          onClick={(e) => {
            e.stopPropagation();
            setIsMobileMenuOpen(!isMobileMenuOpen);
          }}
        >
          {isMobileMenuOpen ? <IoClose /> : <IoMenu />}
        </button>
      </div>

      {/* Portaled Mobile Sidebar */}
      {typeof document !== 'undefined' &&
        createPortal(
          <>
            {/* Blurred Backdrop */}
            <div
              onClick={() => setIsMobileMenuOpen(false)}
              className={`md:hidden fixed inset-0 bg-black/60 backdrop-blur-md z-[9990] transition-opacity duration-300 ${isMobileMenuOpen
                  ? 'opacity-100 pointer-events-auto'
                  : 'opacity-0 pointer-events-none'
                }`}
            />

            {/* Mobile Sidebar */}
            <div
              className={`md:hidden fixed inset-y-0 right-0 w-[65vw] sm:w-[50vw] bg-[#000001] border-l border-[#131F6D] shadow-xl z-[9999] flex flex-col transition-transform duration-500 ease-out overflow-y-auto ${isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
                }`}
            >
              <div className="flex flex-col pt-12 sm:pt-16 px-4 sm:px-6 pb-6 border-b border-[#131F6D] relative">
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="absolute top-4 sm:top-6 right-4 sm:right-6 bg-[#FFFFFF1A] rounded-[8px] flex items-center justify-center p-2 hover:bg-white/20 transition"
                >
                  <IoClose className="text-white text-xl" />
                </button>

                <div className="flex flex-col gap-3 sm:gap-4 mt-6">
                  <div className="overflow-hidden border-2 border-[#2A65B4] rounded-full w-12 h-12 sm:w-14 sm:h-14">
                    <img src={avatarSrc} className="object-cover w-full h-full" alt="User" />
                  </div>
                  <div>
                    <p className="text-[16px] sm:text-[18px] font-bold text-white leading-tight">
                      {userName}
                    </p>
                    <p className="text-[13px] sm:text-[14px] text-white/50 break-all">
                      {user?.email || '—'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex-1 py-4">
                <MenuItems isMobile onNavigate={handleNavigate} />
              </div>
            </div>
          </>,
          document.body
        )}
    </>
  );
};
